import Checkboxes from 'components/Form/Checkboxes';
import InputHidden from 'components/Form/InputHidden';
import InputIncremental from 'components/Form/InputIncremental';
import Radio from 'components/Form/Radio';
import SubmitButton from 'components/Form/SubmitButton';
import Stacked from 'components/Layout/Stacked';
import LocaleImage from 'components/LocaleImage';
import {FC, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {c, jt, t} from 'ttag';
import {FormFields} from 'types/utils';
import getPreferredLanguage from 'utils/locale';

import organizationEN from '../assets/images/organization_en.png';
import organizationJA from '../assets/images/organization_ja.png';
import PlanChangeFooter from '../footer';
import BillingChangeSubscriptionHeader from '../Header';
import parseChangeSubscriptionFormData from '../parse-change-subscription-form-data';
import PriceCalculation from '../PriceCalculation';
import {
    getAdditionalOptions,
    getIntergrationOptions,
    getTermOptionRadio,
} from '../selectableOptions';
import TabNavigation from '../TabNavigation';
import {
    BillingPlanIntegrationsPriceList,
    BillingPlanOption,
    BillingPlanOptionsPriceList,
    BillingScreenName,
    ParsedSearchParams,
    Term,
    TermOptions,
} from '../types';
import useGetOrganizationPrices from './services';

type PageOrganizationFormData = FormFields<
    {[K in BillingPlanOption]: boolean} & {
        activeClients: number;
        billingFrequency: Term;
        plan_code: 'organization_2';
        teamMembers: number;
    }
>;

type PageOrganizationPrivateProps = {
    isAuthenticated: boolean;
    onSuccess: (url: string) => void;
    onTabChange: (tag: BillingScreenName) => void;
};

type PageOrganizationPublicProps = {
    integrationsPrices: BillingPlanIntegrationsPriceList;
    optionPrices: BillingPlanOptionsPriceList;
    termOptions: TermOptions;
};

const PageOrganization: FC<
    PageOrganizationPrivateProps & PageOrganizationPublicProps
> = ({
    integrationsPrices,
    isAuthenticated,
    onSuccess,
    onTabChange,
    optionPrices,
    termOptions,
}) => {
    const language = getPreferredLanguage();
    const methods = useForm<PageOrganizationFormData>({
        defaultValues: {
            activeClients: 10,
            billingFrequency: 'monthly',
            plan_code: 'organization_2',
            teamMembers: 1,
        },
        shouldUnregister: true,
    });

    const {getValues, handleSubmit, watch} = methods;

    const [searchParams, setSearchParams] = useState<ParsedSearchParams>(() =>
        parseChangeSubscriptionFormData(getValues()),
    );
    const {data, isError, isLoading, isSuccess} = useGetOrganizationPrices({
        searchParams,
    });

    watch((formData) => {
        setSearchParams(parseChangeSubscriptionFormData(formData));
    });

    const termOptionRadio = getTermOptionRadio(termOptions);
    const hasTermHelpText = termOptions.some((option) => option.disabled);

    const additionlOptions = getAdditionalOptions(optionPrices);

    const integrationsOptions = getIntergrationOptions(integrationsPrices);

    const onSubmit = (formData: PageOrganizationFormData) => {
        onSuccess(
            `/billing/plan/checkout/?${new URLSearchParams(
                parseChangeSubscriptionFormData(formData),
            ).toString()}`,
        );
    };

    const pricingDetailsLink = (
        <a
            href={`https://www.makeleaps.com/${language}/pricing-detail`}
            key="decreasing-per-client-rate"
        >{t`decreasing per-client rate`}</a>
    );

    // LINK HREF NOT IMPLEMENTED
    const findOutMoreLink = (
        <a className="text-xs" href="/" key="find-out-more">
            {t`(Find out more)`}
        </a>
    );

    return (
        <div>
            <BillingChangeSubscriptionHeader
                isAuthenticated={isAuthenticated}
            />
            <TabNavigation activeTab="organization" onTabChange={onTabChange} />
            <div className="grid w-max max-w-full grid-cols-2 bg-green-10">
                <div className="flex w-full flex-col items-center p-8">
                    <LocaleImage
                        className="max-h-48 max-w-48"
                        src={{en: organizationEN, ja: organizationJA}}
                    />
                    <div className="my-4 text-center">
                        {t`Enable your whole company to coordinate and collaborate with MakeLeaps for Organizations. Automate your entire invoicing workflow and easily manage invoicing and billing for all of your customers.`}
                    </div>
                    <a
                        href={`https://www.makeleaps.com/${language}/pricing-detail`}
                    >
                        {t`Find out more`}
                    </a>
                </div>
                <div className="w-full">
                    <FormProvider {...methods}>
                        <form className="p-4" onSubmit={handleSubmit(onSubmit)}>
                            <InputHidden
                                name="plan_code"
                                value="organization_2"
                            />
                            <Stacked className="w-full">
                                <Stacked className="bg-green-50 p-4">
                                    <InputIncremental
                                        helpText={t`¥800/Team Member/Month`}
                                        label={c('Billing').t`Team Members`}
                                        max={100_000_000}
                                        min={1}
                                        name="teamMembers"
                                        required={true}
                                        requiredType="asterisk"
                                    />
                                    <InputIncremental
                                        helpText={jt`first 10 free, then at a ${pricingDetailsLink}`}
                                        label={t`Active Clients`}
                                        max={100_000_000}
                                        min={10}
                                        name="activeClients"
                                        required={true}
                                        requiredType="asterisk"
                                        step={5}
                                    />
                                </Stacked>
                                <div>
                                    <div>{jt`Options ${findOutMoreLink}`}</div>
                                    <Checkboxes options={additionlOptions} />
                                </div>
                                <div>
                                    <div>{t`Integrations`}</div>
                                    <Checkboxes options={integrationsOptions} />
                                </div>
                                <Radio
                                    helpText={
                                        hasTermHelpText
                                            ? t`(This plan only supports monthly payment)`
                                            : undefined
                                    }
                                    label={t`Billing Frequency`}
                                    name="billingFrequency"
                                    options={termOptionRadio}
                                    required={true}
                                    requiredType="asterisk"
                                />
                                <div className="grid rounded bg-green-200">
                                    <PriceCalculation
                                        isError={isError}
                                        isLoading={isLoading}
                                        isSuccess={isSuccess}
                                        value={data?.amount}
                                    />
                                    <SubmitButton disabled={isError}>
                                        {t`Subscribe`}
                                    </SubmitButton>
                                </div>
                            </Stacked>
                        </form>
                    </FormProvider>
                </div>
            </div>
            <PlanChangeFooter />
        </div>
    );
};

export default PageOrganization;
